import { _CO } from "../../../utils/constants";
import { updateNodeDataAndReturn } from "../sales-block/utils";
import {
  ARRAY_INCLUDE_RATE_DIESEL,
  DIESEL_IMPACT,
  EXCAVATOR_LRU_RS,
  EXCAVATOR_LRU_RS_T,
  EXCAVATOR_SUBCONTRACTOR,
  LORRIES_TRANSPORT_RS,
  LORRIES_TRANSPORT_RS_T,
  PLUMS_COST_RS,
  PLUMS_RS_T,
  PROD_LRL_WASTE_TONS,
} from "./AgGridQuarryingExcavator";

/**
 * @desc Process rows with themes included in ARRAY_INCLUDE_RATE_DIESEL
 *
 * @param {Object} data - Row data
 * @param {Object} api - AG Grid API
 * @param {Object} node - Row node
 * @param {number} columnIndex - Month index (1-12)
 *
 * @returns {number} - Updated rate value with diesel impact
 */
export const processRowsIncludeRateDiesel = (data, api, node, columnIndex) => {
  const baseRate = Number(data?.rate || 0);
  const fiscalMonthIndex = _CO[columnIndex];

  let cumul = 0;

  // Find the diesel impact row in the same parent group
  const dieselImpactRow = api
    .getRowNode(node?.id)
    ?.parent?.allLeafChildren?.find(
      (node) => node?.data?.subThemes === DIESEL_IMPACT,
    );

  if (dieselImpactRow) {
    // Calculate cumulative diesel impact up to the current month
    for (let j = 1; j <= columnIndex; j++) {
      const monthKey = _CO[j];
      const monthImpact = Number(dieselImpactRow?.data?.[monthKey] || 0);
      cumul += monthImpact;
    }
  }

  const newValue = baseRate + cumul;

  return updateNodeDataAndReturn(api, node?.id, fiscalMonthIndex, newValue);
};

/**
 * @desc Calculate the total cost for ARRAY_INCLUDE_TOTAL_COST_VALUE
 *
 * @param {Object} data - Row data
 * @param {Object} api - AG Grid API
 * @param {Object} node - Row node
 * @param {number} columnIndex - Month index (1-12)
 *
 * @returns {number} - The total cost
 */
export const processRowsTotalCostRsT = (data, api, node, columnIndex) => {
  const fiscalMonthIndex = _CO[columnIndex];
  const subTheme = data?.subThemes;
  let totalValue = 0;
  const allRows = [];

  api.forEachNode((rowNode) => {
    if (rowNode?.data) {
      allRows.push(rowNode.data);
    }
  });

  const targetThemes = [...ARRAY_INCLUDE_RATE_DIESEL, PLUMS_RS_T];

  for (const theme of targetThemes) {
    // Find the row with matching theme and subTheme
    const matchingRow = allRows.find(
      (row) => row?.theme === theme && row?.subThemes === subTheme,
    );

    if (matchingRow) {
      totalValue += Number(matchingRow?.[fiscalMonthIndex] || 0);
    }
  }

  return updateNodeDataAndReturn(api, node?.id, fiscalMonthIndex, totalValue);
};

/**
 * @desc Calculate the final value for the given data
 *
 * @param {Object} data - Row data
 * @param {Object} api - AG Grid API
 * @param {Object} node - Row node
 * @param {number} columnIndex - Month index (1-12)
 *
 * @returns {number} - The final value
 */
export const processCalculateFinalValue = (data, api, node, columnIndex) => {
  const fiscalMonthIndex = _CO[columnIndex];
  const subTheme = data?.subThemes;

  const allRows = [];
  api.forEachNode((rowNode) => {
    if (rowNode?.data) {
      allRows.push(rowNode.data);
    }
  });

  // Find the production row (tonnage)
  const prodRow = allRows.find(
    (row) => row?.theme === PROD_LRL_WASTE_TONS && row?.subThemes === subTheme,
  );

  if (!prodRow) return 0;

  const tonnage = Number(prodRow?.[fiscalMonthIndex] || 0);
  let totalValue = 0;

  switch (data?.theme) {
    case PLUMS_COST_RS: {
      /**
       * @info Find PLUMS_RS_T and EXCAVATOR_SUBCONTRACTOR rows
       */
      const plumsRow = allRows.find(
        (row) => row?.theme === PLUMS_RS_T && row?.subThemes === subTheme,
      );

      const excavatorSubcontractorRow = allRows.find(
        (row) =>
          row?.theme === EXCAVATOR_SUBCONTRACTOR && row?.subThemes === subTheme,
      );

      /**
       * @info Calculate PROD_LRL_WASTE_TONS * (PLUMS_RS_T + EXCAVATOR_SUBCONTRACTOR)
       */
      const plumsValue = Number(plumsRow?.[fiscalMonthIndex] || 0);
      const excavatorValue = Number(
        excavatorSubcontractorRow?.[fiscalMonthIndex] || 0,
      );

      totalValue = tonnage * (plumsValue + excavatorValue);
      break;
    }

    case LORRIES_TRANSPORT_RS: {
      /**
       * @info Find LORRIES_TRANSPORT_RS_T row
       */
      const lorryTransportRow = allRows.find(
        (row) =>
          row?.theme === LORRIES_TRANSPORT_RS_T && row?.subThemes === subTheme,
      );

      /**
       * @info Calculate PROD_LRL_WASTE_TONS * LORRIES_TRANSPORT_RS_T
       */
      const lorryTransportValue = Number(
        lorryTransportRow?.[fiscalMonthIndex] || 0,
      );

      totalValue = tonnage * lorryTransportValue;
      break;
    }

    case EXCAVATOR_LRU_RS: {
      /**
       * @info Find EXCAVATOR_LRU_RS_T row
       */
      const excavatorLRURow = allRows.find(
        (row) =>
          row?.theme === EXCAVATOR_LRU_RS_T && row?.subThemes === subTheme,
      );

      /**
       * @info Calculate PROD_LRL_WASTE_TONS * EXCAVATOR_LRU_RS_T
       */
      const excavatorLRUValue = Number(
        excavatorLRURow?.[fiscalMonthIndex] || 0,
      );

      totalValue = tonnage * excavatorLRUValue;
      break;
    }

    default:
      return 0;
  }

  return updateNodeDataAndReturn(api, node?.id, fiscalMonthIndex, totalValue);
};
